import 'text-encoding-polyfill'
import Joi from 'joi'

interface ValidateAccountCreationInterface {
  firstName: string
  lastName: string
  phoneNumber: string
}

interface ValidateCardApplicationInterface {
  firstName: string
  lastName: string
}

// does not accept numbers
const STRING_WITH_ONLY_ALPHABETICAL_CHARACTERS = /^[a-zA-Z\s-]+$/
// individual validations

const joiFirstNameValidationObject = Joi.string()
  .min(1)
  .max(50)
  .regex(STRING_WITH_ONLY_ALPHABETICAL_CHARACTERS)
  .required()
  .messages({
    'string.base': `First Name should only contain text`,
    'string.empty': `First Name cannot be an empty field`,
    'string.min': `First Name should at least be 3 characters long`,
    'string.max': `First Name should less than 50 characters long`,
    'string.pattern.base': `First Name can only contain alphabetical characters`,
    'any.required': `First Name is a required field`,
  })

const firstNameValidation = (firstName: string) => joiFirstNameValidationObject.validate(firstName)

const joiLastNameValidationObject = Joi.string()
  .min(1)
  .max(50)
  .regex(STRING_WITH_ONLY_ALPHABETICAL_CHARACTERS)
  .required()
  .messages({
    'string.base': `Last Name should only contain text`,
    'string.empty': `Last Name cannot be an empty field`,
    'string.min': `Last Name should at least be 1 characters long`,
    'string.max': `Last Name should be less than 50 characters long`,
    'string.pattern.base': `Last Name can only contain alphabetical characters`,
    'any.required': `Last Name is a required field`,
  })
const lastNameValidation = (lastName: string) => joiLastNameValidationObject.validate(lastName)

export const joiEmailValidationObject = Joi.string()
  .email({ minDomainSegments: 2, tlds: { allow: false } })
  .required()
  .messages({
    'string.email': 'Please enter a valid email address.',
    'string.base': 'The email provided is not valid.',
    'string.empty': 'Email address cannot be empty.',
    'any.required': 'Email address is required.',
  })

/**
 * Currently only used here where the phone number is formatted as (XXX) XXX-XXXX
 * https://github.com/Acre-Rewards/acre-app/blob/main/src/app/onboarding/AccountCreation.tsx#L105-L106
 */
const phoneValidationObject = Joi.string()
  .regex(/^\(\d{3}\) \d{3}-\d{4}$/)
  .messages({
    'string.pattern.base': 'Phone number is not valid.',
    'string.empty': 'Phone number cannot be empty',
  })
  .required()

const phoneValidation = (phone: string) => phoneValidationObject.validate(phone)

// other functions

export function validateAccountCreation({
  firstName,
  lastName,
  phoneNumber,
}: ValidateAccountCreationInterface) {
  const _firstNameValidation = firstNameValidation(firstName)
  const _lastNameValidation = lastNameValidation(lastName)
  const _phoneValidation = phoneValidation(phoneNumber)
  return {
    firstName: _firstNameValidation.error?.message,
    lastName: _lastNameValidation.error?.message,
    phoneNumber: _phoneValidation.error?.message,
  } as const
}

export function validateCardApplication({ firstName, lastName }: ValidateCardApplicationInterface) {
  const _firstNameValidation = firstNameValidation(firstName)
  const _lastNameValidation = lastNameValidation(lastName)
  return {
    firstName: _firstNameValidation.error?.message,
    lastName: _lastNameValidation.error?.message,
  }
}
